import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLocale } from "@/hooks/useLocale";
import {
  useMarkDisputesSettledMutation,
  type ISettleableDispute,
} from "@/services/(company)/payrollDisputesApi";
import {
  commonSettlementMethods,
  settlementNeedsReference,
  type ManualSettlementMethod,
} from "./settlement-methods";

/**
 * Settling disputes by hand (B47): how, the reference that traces it, and a
 * note. The amount is each dispute's own adjustment — shown, not typed.
 */
export const MarkSettledDialog = ({
  open,
  disputes,
  onClose,
  onDone,
}: {
  open: boolean;
  disputes: ISettleableDispute[];
  onClose: () => void;
  onDone: () => void;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const { formatNumber } = useLocale();
  const [markSettled, { isLoading }] = useMarkDisputesSettledMutation();

  const methods = useMemo(
    () => commonSettlementMethods(disputes.map((d) => d.adjustmentAmount)),
    [disputes],
  );
  const [method, setMethod] = useState<ManualSettlementMethod | "">("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setMethod(methods.length === 1 ? methods[0] : "");
    setReference("");
    setNote("");
  }, [open, methods]);

  const total = disputes.reduce((s, d) => s + d.adjustmentAmount, 0);
  const currency = disputes[0]?.currencyCode ?? "";
  const needsRef = method !== "" && settlementNeedsReference(method);
  const canSubmit =
    method !== "" && (!needsRef || reference.trim().length > 0) && !isLoading;

  const submit = async () => {
    if (method === "") return;
    try {
      const res = await markSettled({
        disputeIds: disputes.map((d) => d.id),
        method,
        reference: reference.trim() || undefined,
        note: note.trim() || undefined,
      }).unwrap();
      toast.success(
        t("company:payroll_disputes.marked_settled", {
          count: res.data.settledCount,
        }),
      );
      onDone();
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? t("company:payrolls.failed_to_mark_settled")),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("company:payrolls.mark_as_settled")}</DialogTitle>
          <DialogDescription>
            {t("company:payroll_disputes.settle_summary", {
              count: disputes.length,
              amount: `${currency} ${formatNumber(total, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`.trim(),
            })}
          </DialogDescription>
        </DialogHeader>

        {methods.length === 0 ? (
          <p className="text-sm text-danger">
            {t("company:payroll_disputes.settle_mixed_selection")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("company:payroll_disputes.settlement_method")}
              </label>
              <Select
                value={method}
                onValueChange={(v) => setMethod(v as ManualSettlementMethod)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue
                    placeholder={t("company:payroll_disputes.choose_method")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {methods.map((m) => (
                    <SelectItem key={m} value={m}>
                      {t(`company:payroll_disputes.method_${m.toLowerCase()}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {method !== "" && method !== "NO_PAYMENT_DUE" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">
                  {needsRef
                    ? t("company:payroll_disputes.reference_required")
                    : t("company:payroll_disputes.reference_optional")}
                </label>
                <input
                  type="text"
                  value={reference}
                  maxLength={100}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={t(
                    "company:payroll_disputes.reference_placeholder",
                  )}
                  className="h-9 rounded-xs border border-border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("company:payroll_disputes.settlement_note_optional")}
              </label>
              <textarea
                value={note}
                rows={2}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
                className="resize-none rounded-xs border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("common:buttons.cancel")}
          </Button>
          <Button onClick={submit} disabled={!canSubmit} isLoading={isLoading}>
            {t("company:payrolls.mark_as_settled")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
